import SEO from '../components/SEO';
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiClock, FiUser, FiTag, FiArrowLeft, FiShare2, FiHeart, FiExternalLink, FiCalendar } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { fetchArticleById, fetchAllArticles, getImageSrc, formatMarathiDate } from '../apiService';

const ArticleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [isLiked, setIsLiked] = useState(false);

  useEffect(() => {
    loadArticle();
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (article && article.tags && article.tags.length > 0) {
      loadRelatedArticles();
    }
  }, [article]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      const articleData = await fetchArticleById(id);
      if (articleData) {
        setArticle(articleData);
      } else {
        setError('Article not found');
      }
    } catch (err) {
      console.error('Error loading article:', err);
      setError('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedArticles = async () => {
    try {
      const result = await fetchAllArticles(1, 6);
      // Filter by similar tags
      const filtered = result.articles.filter(relatedArticle => 
        relatedArticle._id !== id && 
        relatedArticle.tags && 
        relatedArticle.tags.some(tag => article.tags.includes(tag))
      );
      setRelatedArticles(filtered.slice(0, 3));
    } catch (err) {
      console.error('Error loading related articles:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('mr-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.content[0] || '',
          url: window.location.href
        });
      } catch (err) {
        console.log('Error sharing:', err);
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied to clipboard!');
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
  };

  const calculateReadTime = (content) => {
    if (!content || !Array.isArray(content)) return '5 मिनिटे';
    
    const fullText = content.join(' ');
    const wordsPerMinute = 200;
    const words = fullText.split(' ').length;
    const minutes = Math.ceil(words / wordsPerMinute);
    
    if (minutes === 1) return '1 मिनिट';
    return `${minutes} मिनिटे`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded mb-4 w-1/4"></div>
              <div className="h-12 bg-gray-300 rounded mb-6"></div>
              <div className="h-64 bg-gray-300 rounded mb-6"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                <div className="h-4 bg-gray-300 rounded w-4/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-3xl font-bold text-red-800 mb-4">लेख सापडला नाही</h2>
          <p className="text-red-600 mb-6">{error}</p>
          <div className="space-x-4">
            <button 
              onClick={() => navigate(-1)}
              className="bg-red-800 text-white px-6 py-2 rounded-lg hover:bg-red-900 transition-colors"
            >
              मागे जा
            </button>
            <Link 
              to="/allarticles"
              className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors inline-block"
            >
              सर्व लेख
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-20">
      <SEO title={article.title} description={article.content?.[0] ? article.content[0].replace(/<[^>]+>/g, '').slice(0, 155) : 'डॉ. राजेंद्र शेंडे यांचा लेख.'} image={article.thumbnail} canonical={`/article/${article.slug || article._id}`} type="article" article={{ publishedTime: article.date, author: 'article' }} breadcrumbs={[{ name: 'मुख्यपृष्ठ', url: '/' }, { name: 'लेख', url: '/allarticles' }, { name: article.title, url: `/article/${article.slug || article._id}` }]} />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.div 
            className="mb-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <button 
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-red-800 hover:text-red-900 font-medium transition-colors"
            >
              <FiArrowLeft className="w-5 h-5" />
              मागे जा
            </button>
          </motion.div>

          {/* Article Header */}
          <motion.article 
            className="bg-white border-2 border-red-100 rounded-2xl shadow-xl overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Hero Image */}
            <div className="relative h-64 md:h-96 overflow-hidden">
              <img loading="lazy" 
                src={getImageSrc(article.thumbnail)} 
                alt={article.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              
              {/* Title Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <motion.h1 
                  className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  {article.title}
                </motion.h1>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 md:p-8">
              {/* Meta Information */}
              <motion.div 
                className="flex flex-wrap items-center gap-4 md:gap-6 text-red-700 mb-6 pb-6 border-b border-red-200"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <div className="flex items-center gap-2">
                  <FiUser className="w-5 h-5 text-red-600" />
                  <span className="font-medium">{article.author || 'Admin'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FiCalendar className="w-5 h-5 text-red-600" />
                  <span>{formatDate(article.date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FiClock className="w-5 h-5 text-red-600" />
                  <span>{calculateReadTime(article.content)}</span>
                </div>
              </motion.div>

              {/* Tags */}
              {article.tags && article.tags.length > 0 && (
                <motion.div 
                  className="flex flex-wrap gap-2 mb-8"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.7 }}
                >
                  {article.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium"
                    >
                      <FiTag className="w-3 h-3" />
                      {tag}
                    </span>
                  ))}
                </motion.div>
              )}

              {/* Article Content */}
              <motion.div 
                className="prose prose-lg max-w-none"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                {article.content && article.content.map((paragraph, index) => (
                  <p key={index} className="mb-6 text-red-800 leading-relaxed text-lg">
                    {paragraph}
                  </p>
                ))}
              </motion.div>

              {/* Additional Images */}
              {article.images && article.images.length > 0 && (
                <motion.div 
                  className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  {article.images.map((image, index) => (
                    <div key={index} className="rounded-xl overflow-hidden shadow-lg border-2 border-red-100">
                      <img loading="lazy" 
                        src={getImageSrc(image)} 
                        alt={`${article.title} - Image ${index + 1}`}
                        className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ))}
                </motion.div>
              )}

              {/* External Link */}
              {article.AnyLink && (
                <motion.div 
                  className="mt-8 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.1 }}
                >
                  <p className="text-sm text-blue-700 mb-2 font-medium">संबंधित दुवा:</p>
                  <a 
                    href={article.AnyLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-900 font-medium transition-colors"
                  >
                    {article.AnyLink}
                    <FiExternalLink className="w-4 h-4" />
                  </a>
                </motion.div>
              )}

              {/* Action Buttons */}
              <motion.div 
                className="flex items-center justify-between pt-8 mt-8 border-t border-red-200"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.2 }}
              >
                <div className="flex items-center gap-4">
                  <button 
                    onClick={handleLike}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                      isLiked 
                        ? 'bg-red-800 text-white shadow-lg' 
                        : 'bg-red-100 text-red-800 hover:bg-red-50 hover:text-red-700'
                    }`}
                  >
                    <FiHeart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
                    आवडले
                  </button>
                  <button 
                    onClick={handleShare}
                    className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-800 rounded-lg font-medium hover:bg-blue-50 hover:text-blue-700 transition-all duration-300"
                  >
                    <FiShare2 className="w-5 h-5" />
                    शेअर करा
                  </button>
                </div>
                <Link 
                  to="/allarticles"
                  className="text-red-800 hover:text-red-900 font-medium transition-colors"
                >
                  सर्व लेख पहा
                </Link>
              </motion.div>
            </div>
          </motion.article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <motion.div 
              className="mt-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.4 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-red-800 mb-8 text-center">
                संबंधित लेख
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((relatedArticle, index) => (
                  <motion.div
                    key={relatedArticle._id}
                    className="bg-white border-2 border-red-100 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 1.6 + index * 0.1 }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img loading="lazy" 
                        src={getImageSrc(relatedArticle.thumbnail)} 
                        alt={relatedArticle.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-red-800 mb-2 line-clamp-2 group-hover:text-red-600 transition-colors">
                        {relatedArticle.title}
                      </h3>
                      <p className="text-red-700 text-sm mb-3 line-clamp-2">
                        {Array.isArray(relatedArticle.content) 
                          ? relatedArticle.content.join(' ').substring(0, 100) + '...'
                          : relatedArticle.content?.substring(0, 100) + '...'
                        }
                      </p>
                      <Link 
                        to={`/article/${relatedArticle.slug || relatedArticle._id}`}
                        className="inline-flex items-center gap-1 text-red-800 hover:text-red-900 font-medium text-sm transition-colors"
                      >
                        वाचा
                        <FiArrowLeft className="w-4 h-4 transform rotate-180" />
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .prose p {
          margin-bottom: 1.5rem;
          line-height: 1.8;
        }
        .prose p:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </div>
  );
};

export default ArticleDetails;